import React, { useState, useMemo, useCallback } from 'react'
import './index.scss'

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'Veri bulunamadı',
  emptyIcon = 'zmdi-search',
  rowKey = 'id',
  onRowClick,
  className = '',
  itemLabel = 'kayıt',
  pagination = {},
  striped = false,
  hoverable = true,
  // Server-side props
  serverSide = false,
  totalCount = 0,
  onPageChange = null,
  onLimitChange = null
}) => {
  const {
    enabled: paginationEnabled = true,
    defaultItemsPerPage = 10,
    itemsPerPageOptions = [10, 25, 50, 100],
    showInfo = true
  } = pagination

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage)

  // Reset to page 1 when data changes (only client-side)
  useMemo(() => {
    if (!serverSide) {
      setCurrentPage(1)
    }
  }, [data.length, serverSide])

  // Pagination calculations
  const totalItems = serverSide ? totalCount : data.length
  const totalPages = paginationEnabled ? Math.ceil(totalItems / itemsPerPage) : 1

  // Use data directly if server-side, otherwise slice
  const paginatedData = useMemo(() => {
    if (serverSide || !paginationEnabled) return data

    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return data.slice(startIndex, endIndex)
  }, [data, serverSide, paginationEnabled, currentPage, itemsPerPage])

  // Page change handler
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      if (serverSide && onPageChange) {
        onPageChange(page)
      }
    }
  }, [totalPages, serverSide, onPageChange])

  // Items per page change handler
  const handleItemsPerPageChange = useCallback((e) => {
    const newLimit = parseInt(e.target.value)
    setItemsPerPage(newLimit)
    setCurrentPage(1)
    if (serverSide && onLimitChange) {
      onLimitChange(newLimit)
    }
  }, [serverSide, onLimitChange])

  // Render page numbers
  const renderPageNumbers = () => {
    const pages = []
    const maxVisible = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let endPage = Math.min(totalPages, startPage + maxVisible - 1)

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      )
    }

    return pages
  }

  // Get cell value
  const getCellValue = (row, column) => {
    const value = row[column.key]
    if (column.render) {
      return column.render(value, row)
    }
    return value ?? '-'
  }

  // Get row key value
  const getRowKey = (row, index) => {
    return row[rowKey] ?? index
  }

  // Loading state
  if (loading) {
    return (
      <div className={`data-table-wrapper ${className}`}>
        <div className="data-table-loading">
          <div className="loading-spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={`data-table-wrapper ${className}`}>
        <div className="data-table-empty">
          <i className={`zmdi ${emptyIcon}`}></i>
          <p>{emptyMessage}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`data-table-wrapper ${className}`}>
      <div className="data-table-responsive">
        <table className={`data-table ${striped ? 'striped' : ''} ${hoverable ? 'hoverable' : ''}`}>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key || index}
                  className={column.headerClassName || column.className || ''}
                  style={{
                    width: column.width,
                    textAlign: column.align || 'left'
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={getRowKey(row, rowIndex)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'clickable' : ''}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={column.key || colIndex}
                    className={column.className || ''}
                    style={{ textAlign: column.align || 'left' }}
                  >
                    {getCellValue(row, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginationEnabled && totalItems > 0 && (
        <div className="data-table-pagination">
          {showInfo && (
            <div className="pagination-info">
              <span>
                Toplam <strong>{totalItems}</strong> {itemLabel}
                {totalPages > 1 && (
                  <> | Sayfa <strong>{currentPage}</strong> / <strong>{totalPages}</strong></>
                )}
              </span>
            </div>
          )}

          <div className="pagination-controls">
            {totalPages > 1 && (
              <div className="pagination-buttons">
                <button
                  className="pagination-btn pagination-nav"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="zmdi zmdi-chevron-left"></i>
                </button>

                {renderPageNumbers()}

                <button
                  className="pagination-btn pagination-nav"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <i className="zmdi zmdi-chevron-right"></i>
                </button>
              </div>
            )}

            <div className="pagination-limit">
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="limit-select"
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataTable

